import { useSEO, generateSchema } from '../../utils/seo';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BlogPage = () => {
  const { i18n } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoshino-village-project.com';

  useSEO({
    title: i18n.language === 'ja' ? 'ブログ | Yoshino Village Project' : 'Blog | Yoshino Village Project',
    description: i18n.language === 'ja' 
      ? 'プロジェクトの最新活動や地域の魅力をお届けします。吉野での地域共創の取り組みや、イベント情報、活動報告を発信しています。'
      : 'Delivering the latest project activities and regional attractions. Sharing regional co-creation efforts in Yoshino, event information, and activity reports.',
    keywords: '吉野, ブログ, 活動報告, イベント, 地域共創, プロジェクト, お知らせ',
    canonical: `${siteUrl}/blog`,
    schema: generateSchema.collectionPage(
      `${siteUrl}/blog`,
      i18n.language === 'ja' ? 'ブログ | Yoshino Village Project' : 'Blog | Yoshino Village Project',
      i18n.language === 'ja' 
        ? 'プロジェクトの最新活動や地域の魅力をお届けします。吉野での地域共創の取り組みや、イベント情報、活動報告を発信しています。'
        : 'Delivering the latest project activities and regional attractions. Sharing regional co-creation efforts in Yoshino, event information, and activity reports.'
    ),
  });

  const blogs = [
    {
      id: 1,
      title: '春の植樹イベントを開催しました',
      titleEn: 'Spring Tree Planting Event Held',
      date: '2024年3月15日',
      dateEn: 'March 15, 2024',
      category: 'イベント',
      categoryEn: 'Event',
      excerpt: '3月10日、吉野山にて春の植樹イベントを開催しました。地域住民や参加者約50名が集まり、桜の苗木を植樹しました。',
      excerptEn: 'On March 10, we held a spring tree planting event at Mt. Yoshino. About 50 local residents and participants gathered to plant cherry tree saplings.',
      image: 'https://public.readdy.ai/ai/img_res/fd4e84a6fa83fc526a0146263a7bee7f.jpg',
    },
    {
      id: 2,
      title: '地域の伝統工芸を学ぶワークショップ',
      titleEn: 'Workshop on Traditional Local Crafts',
      date: '2024年3月8日',
      dateEn: 'March 8, 2024',
      category: '活動報告',
      categoryEn: 'Activity Report',
      excerpt: '吉野の伝統工芸である木工技術を学ぶワークショップを開催。参加者は職人から直接技術を学び、実際に作品を制作しました。',
      excerptEn: 'We held a workshop to learn woodworking techniques, a traditional craft of Yoshino. Participants learned techniques directly from craftsmen and created actual works.',
      image: 'https://readdy.ai/api/search-image?query=traditional%20japanese%20craft%20workshop%20with%20artisans%20teaching%20local%20crafts%2C%20hands%20working%20on%20traditional%20materials%2C%20cultural%20heritage%20activity%2C%20warm%20indoor%20lighting%2C%20focused%20atmosphere%2C%20simple%20clean%20background&width=500&height=350&seq=blog-craft-workshop&orientation=landscape',
    },
    {
      id: 3,
      title: '吉野の四季を巡るフォトツアー',
      titleEn: 'Photo Tour Through Four Seasons of Yoshino',
      date: '2024年2月28日',
      dateEn: 'February 28, 2024',
      category: 'お知らせ',
      categoryEn: 'Announcement',
      excerpt: '4月に開催予定のフォトツアーの参加者を募集中です。プロカメラマンと共に吉野の美しい風景を撮影します。',
      excerptEn: 'We are recruiting participants for a photo tour scheduled for April. Capture Yoshino\'s beautiful scenery with professional photographers.',
      image: 'https://readdy.ai/api/search-image?query=photography%20tour%20group%20in%20yoshino%20mountains%20capturing%20beautiful%20seasonal%20landscapes%2C%20people%20with%20cameras%20enjoying%20nature%2C%20outdoor%20activity%2C%20natural%20lighting%2C%20scenic%20mountain%20views%2C%20simple%20clean%20background&width=500&height=350&seq=blog-photo-tour&orientation=landscape',
    },
    {
      id: 4,
      title: '環境保全活動の年次報告',
      titleEn: 'Annual Environmental Conservation Report',
      date: '2024年2月20日',
      dateEn: 'February 20, 2024',
      category: '活動報告',
      categoryEn: 'Activity Report',
      excerpt: '2023年度の環境保全活動をまとめました。植樹本数、清掃活動の実績、生態系保護の取り組みについて報告します。',
      excerptEn: 'We have compiled our 2023 environmental conservation activities. Reporting on tree planting numbers, cleanup activity results, and ecosystem protection efforts.',
      image: 'https://readdy.ai/api/search-image?query=environmental%20conservation%20activity%20in%20forest%20with%20volunteers%20cleaning%20and%20protecting%20nature%2C%20teamwork%20outdoor%20activity%2C%20natural%20lighting%2C%20green%20forest%20background%2C%20simple%20clean%20background&width=500&height=350&seq=blog-environment&orientation=landscape',
    },
    {
      id: 5,
      title: '新メンバー紹介：教育プログラム担当',
      titleEn: 'New Member Introduction: Educational Programs',
      date: '2024年2月15日',
      dateEn: 'February 15, 2024',
      category: 'お知らせ',
      categoryEn: 'Announcement',
      excerpt: '新たに教育プログラム担当として高橋由美さんが加わりました。子どもたちへの教育活動を強化していきます。',
      excerptEn: 'Yumi Takahashi has joined as our new educational programs coordinator. We will strengthen educational activities for children.',
      image: 'https://readdy.ai/api/search-image?query=educational%20program%20for%20children%20in%20rural%20japanese%20setting%20with%20teacher%20and%20students%20learning%20together%2C%20warm%20atmosphere%2C%20indoor%20classroom%20activity%2C%20natural%20lighting%2C%20simple%20clean%20background&width=500&height=350&seq=blog-education&orientation=landscape',
    },
    {
      id: 6,
      title: '冬の交流会を開催しました',
      titleEn: 'Winter Exchange Event Held',
      date: '2024年2月5日',
      dateEn: 'February 5, 2024',
      category: 'イベント',
      categoryEn: 'Event',
      excerpt: '地域住民と移住者の交流を深める冬の交流会を開催。温かい料理を囲みながら、今後の活動について意見交換を行いました。',
      excerptEn: 'We held a winter exchange event to deepen connections between local residents and migrants. We exchanged opinions about future activities over warm food.',
      image: 'https://readdy.ai/api/search-image?query=community%20gathering%20event%20in%20traditional%20japanese%20house%20with%20people%20enjoying%20warm%20food%20and%20conversation%2C%20winter%20season%2C%20cozy%20indoor%20atmosphere%2C%20warm%20lighting%2C%20simple%20clean%20background&width=500&height=350&seq=blog-winter-gathering&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="pt-32 pb-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm text-gray-500 font-light mb-3 tracking-widest">PROJECT BLOG</p>
            <h1 className="text-6xl font-black text-gray-900 mb-4">
              {i18n.language === 'ja' ? 'ブログ' : 'Blog'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {i18n.language === 'ja' 
                ? 'プロジェクトの最新活動や地域の魅力をお届けします'
                : 'Delivering the latest project activities and regional attractions'}
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-8">
            {blogs.map((blog, index) => (
              <motion.div
                key={blog.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/blog/${blog.id}`}
                  className="block bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-2"
                >
                  <div className="h-56">
                    <img
                      src={blog.image}
                      alt={i18n.language === 'ja' ? blog.title : blog.titleEn}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm text-gray-500">
                        {i18n.language === 'ja' ? blog.date : blog.dateEn}
                      </span>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        {i18n.language === 'ja' ? blog.category : blog.categoryEn}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {i18n.language === 'ja' ? blog.title : blog.titleEn}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {i18n.language === 'ja' ? blog.excerpt : blog.excerptEn}
                    </p>
                    <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                      {i18n.language === 'ja' ? '続きを読む' : 'Read More'}
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;